import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Search, Globe, Shield, Star, Zap, Users, CheckCircle, Info, ChevronDown } from 'lucide-react';
import { EXTERNAL_URLS } from '../config';

const EXTENSIONS = [
  { ext: '.com', price: 'R$ 49,90/ano' },
  { ext: '.com.br', price: 'R$ 39,90/ano' },
  { ext: '.net', price: 'R$ 59,90/ano' },
  { ext: '.org', price: 'R$ 59,90/ano' },
  { ext: '.store', price: 'R$ 99,90/ano' },
  { ext: '.online', price: 'R$ 89,90/ano' },
  { ext: '.tech', price: 'R$ 119,90/ano' },
  { ext: '.app', price: 'R$ 129,90/ano' },
];

const BENEFITS = [
  { icon: <Shield size={28} className="text-purple-400" />, title: 'Proteção e Segurança', desc: 'Domínios com DNS seguro, proteção contra roubo e privacidade WHOIS.' },
  { icon: <Zap size={28} className="text-purple-400" />, title: 'Ativação Imediata', desc: 'Registro instantâneo e painel intuitivo para gerenciar seu domínio.' },
  { icon: <Star size={28} className="text-purple-400" />, title: 'Suporte Especializado', desc: 'Equipe pronta para ajudar em cada etapa, 24/7.' },
  { icon: <CheckCircle size={28} className="text-purple-400" />, title: 'Facilidade de Transferência', desc: 'Transfira seu domínio para a Hostul sem complicação.' },
  { icon: <Users size={28} className="text-purple-400" />, title: 'Para todos os perfis', desc: 'Profissionais, empresas, lojas e projetos pessoais.' },
];

const FAQS = [
  {
    question: 'O que é um domínio?',
    answer: 'Domínio é o endereço do seu site na internet, como exemplo.com.br. Ele é único e serve para que as pessoas encontrem sua página facilmente.'
  },
  {
    question: 'Como escolher um bom domínio?',
    answer: 'Prefira nomes curtos, fáceis de lembrar e digitar. Evite números e hífens. Dê preferência ao .com.br ou .com para negócios no Brasil.'
  },
  {
    question: 'Posso registrar mais de um domínio?',
    answer: 'Sim! Você pode registrar quantos domínios quiser, inclusive variações e extensões diferentes para proteger sua marca.'
  },
  {
    question: 'O que é privacidade WHOIS?',
    answer: 'É um serviço que protege seus dados pessoais no registro do domínio, evitando exposição pública de informações como e-mail e telefone.'
  },
  {
    question: 'Como funciona a renovação do domínio?',
    answer: 'Você pode renovar seu domínio anualmente. A Hostul envia lembretes para você não perder o prazo e manter seu endereço ativo.'
  },
  {
    question: 'Consigo transferir um domínio de outro provedor?',
    answer: 'Sim! Basta solicitar a transferência pelo nosso painel e seguir as instruções. Nossa equipe pode ajudar em todo o processo.'
  },
];

export default function DomainRegisterLandingPage() {
  const [domain, setDomain] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  function handleSearch(e) {
    e.preventDefault();
    setSearching(true);
    setTimeout(() => {
      setResult({ available: Math.random() > 0.5 });
      setSearching(false);
    }, 1200);
  }

  return (
    <div className="bg-gradient-to-br from-gray-950 via-purple-950 to-gray-900 min-h-screen flex flex-col">
      <Header />
      {/* HERO + BUSCA */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-20 text-center relative z-10">
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-900/40 via-transparent to-indigo-900/30 pointer-events-none animate-gradient-move" />
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">Registre seu Domínio</h1>
        <p className="text-lg md:text-2xl text-gray-300 max-w-2xl mx-auto mb-8">Garanta sua presença online com um domínio exclusivo, seguro e fácil de gerenciar.</p>
        <form onSubmit={handleSearch} className="max-w-xl mx-auto flex flex-col md:flex-row items-center gap-3 md:gap-0 md:rounded-xl bg-gray-900/80 border border-purple-900 shadow-lg p-2 md:p-3 mb-6 relative z-20">
          <Search size={22} className="text-purple-400 mx-2" />
          <input
            type="text"
            className="flex-1 bg-transparent outline-none text-white placeholder-gray-400 px-2 py-3 md:py-2 text-lg md:text-xl"
            placeholder="seunome.com.br"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            required
            minLength={3}
            maxLength={60}
            autoFocus
          />
          <button type="submit" className="px-6 py-2 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-md hover:from-purple-700 hover:to-indigo-700 text-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed ml-2" disabled={searching}>{searching ? 'Buscando...' : 'Buscar'}</button>
        </form>
        {result && (
          <div className="max-w-xl mx-auto mt-2 animate-fade-in">
            {result.available ? (
              <div className="bg-green-900/80 border border-green-700 text-green-200 rounded-xl py-3 px-6 font-semibold flex items-center justify-center gap-2 shadow-md">
                <CheckCircle size={20} className="text-green-400" /> Domínio disponível! <span className="hidden md:inline">Registre agora e garanta seu endereço exclusivo.</span>
              </div>
            ) : (
              <div className="bg-red-900/80 border border-red-700 text-red-200 rounded-xl py-3 px-6 font-semibold flex items-center justify-center gap-2 shadow-md">
                <Shield size={20} className="text-red-400" /> Domínio indisponível. Tente outra opção ou extensão.
              </div>
            )}
          </div>
        )}
        <style>{`
          @keyframes gradient-move {
            0% { background-position: 0% 50%; }
            100% { background-position: 100% 50%; }
          }
          .animate-gradient-move {
            background-size: 200% 200%;
            animation: gradient-move 8s linear infinite alternate;
          }
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: none; }
          }
          .animate-fade-in { animation: fade-in 0.7s cubic-bezier(.4,0,.2,1) both; }
        `}</style>
      </section>
      {/* SOBRE DOMÍNIO */}
      <section className="py-10 md:py-16 bg-gradient-to-b from-gray-950/80 to-transparent">
        <div className="container mx-auto px-2 md:px-4 max-w-4xl">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 flex items-center gap-2"><Globe size={24} className="text-purple-400" /> O que é um domínio?</h2>
              <p className="text-gray-300 text-lg mb-3">Domínio é o endereço digital da sua marca, empresa ou projeto na internet. Ele é único, fácil de lembrar e essencial para construir sua presença online.</p>
              <ul className="list-disc pl-6 text-gray-400 text-base space-y-1 mb-3">
                <li>Exemplo: <span className="text-purple-300 font-semibold">www.suaempresa.com.br</span></li>
                <li>Permite criar e-mails profissionais: <span className="text-purple-300 font-semibold">contato@suaempresa.com.br</span></li>
                <li>Ajuda a proteger sua marca e identidade digital</li>
              </ul>
              <p className="text-gray-400 text-base">Na Hostul, você registra, transfere e gerencia seus domínios com total segurança, suporte e praticidade.</p>
            </div>
            <div className="flex-1 flex flex-col items-center">
              <div className="bg-gradient-to-br from-purple-800/60 to-indigo-900/60 rounded-2xl p-6 shadow-lg border border-purple-900 w-full max-w-xs">
                <div className="flex items-center gap-2 mb-2"><Info size={18} className="text-purple-300" /> <span className="text-white font-semibold">Dicas para escolher seu domínio:</span></div>
                <ul className="list-disc pl-5 text-gray-300 text-sm space-y-1">
                  <li>Seja curto, simples e fácil de pronunciar</li>
                  <li>Evite números e caracteres especiais</li>
                  <li>Prefira extensões populares (.com, .com.br)</li>
                  <li>Registre variações para proteger sua marca</li>
                </ul>
                <div className="mt-4 text-xs text-gray-400">Dúvidas? Veja o FAQ abaixo ou fale com nosso suporte!</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* EXTENSÕES POPULARES */}
      <section className="py-8 md:py-14">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-xl md:text-2xl font-bold text-white text-center mb-6">Principais extensões de domínio</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {EXTENSIONS.map((e, i) => (
              <div key={i} className="bg-gray-900/80 border border-purple-900 rounded-xl p-5 flex flex-col items-center shadow-md hover:scale-105 transition-transform">
                <span className="text-2xl font-bold text-purple-300 mb-1">{e.ext}</span>
                <span className="text-sm text-gray-300">{e.price}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* BENEFÍCIOS */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-10">Por que registrar seu domínio com a Hostul?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-gray-900/80 rounded-2xl p-6 flex flex-col items-center text-center shadow-lg border border-purple-900">
                <div className="mb-3">{b.icon}</div>
                <h3 className="text-lg font-bold text-purple-200 mb-2">{b.title}</h3>
                <p className="text-gray-300 text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-10 md:py-16 bg-gradient-to-b from-gray-950/80 to-transparent">
        <div className="container mx-auto px-2 md:px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">Perguntas Frequentes sobre Domínios</h2>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <div key={i} className="rounded-xl bg-gray-900/80 border border-purple-900 shadow-md overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-white font-semibold text-lg focus:outline-none hover:bg-purple-900/10 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-panel-${i}`}
                >
                  <span>{faq.question}</span>
                  <span className={`transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}><ChevronDown size={22} /></span>
                </button>
                <div
                  id={`faq-panel-${i}`}
                  className={`px-5 pb-4 text-gray-300 text-base transition-all duration-300 ${openFaq === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
                  style={{ transitionProperty: 'max-height, opacity' }}
                >
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA FINAL */}
      <section className="py-12 md:py-20 text-center">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">Pronto para garantir seu domínio?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">Dê o primeiro passo para sua presença digital. Registre seu domínio com a Hostul e conte com suporte de verdade.</p>
          <a href={EXTERNAL_URLS.cartBase} className="inline-block px-8 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 text-lg transition-all">Registrar agora</a>
        </div>
      </section>
      <Footer />
    </div>
  );
} 
